import React from 'react';
import ParentLayoutUnified from '@/components/layout/ParentLayoutUnified';
import { useParentMessages } from '@/hooks/useParentMessages';
import ConversationListPanel from '@/components/parent-portal/messages/ConversationListPanel';
import MessageThreadPanel from '@/components/parent-portal/messages/MessageThreadPanel';
import { Button } from '@/components/ui/button';
import { PlusCircle, Mail } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useParentDashboard } from '@/hooks/useParentDashboard';
import { useResponsive } from '@/hooks/useResponsive';
import { RESPONSIVE_SPACING } from '@/design-tokens/responsive';
import { cn } from '@/lib/utils';
import { ParentLoadingState } from '@/components/parent-portal/ParentLoadingState';

const ParentPortalMessages: React.FC = () => {
  const {
    conversationId,
    conversations,
    messages,
    newMessage,
    setNewMessage,
    currentConversation,
    searchTerm,
    setSearchTerm,
    loading,
    currentUser,
    handleSendMessage
  } = useParentMessages();
  
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const parentDashboard = useParentDashboard();

  if (loading) {
    return (
      <ParentLayoutUnified
        pageTitle="Messages"
        pageDescription="Communicate with your hive"
      >
        <ParentLoadingState 
          message="Loading messages..." 
          size="md"
          variant="skeleton"
        />
      </ParentLayoutUnified>
    );
  }

  return (
    <ParentLayoutUnified
      pageTitle="Messages"
      pageDescription="Communicate with your hive"
      rightContent={
        <Button 
          onClick={() => navigate('/parent-portal/messages/new')}
          size="sm"
          className="gap-1"
        >
          <PlusCircle className="h-4 w-4" />
          New Message
        </Button>
      }
    >
      <div className={cn(
        "w-full",
        RESPONSIVE_SPACING.container.mobile
      )}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-[calc(100vh-12rem)] lg:h-[calc(100vh-10rem)]">
              {/* Always show conversations on larger screens */}
              <div className={`${conversationId && isMobile ? 'hidden md:block' : ''}`}>
                <ConversationListPanel
                  conversations={conversations}
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  loading={loading}
                  currentConversationId={conversationId}
                  onSelectConversation={(id) => {
                    navigate(`/parent-portal/messages/${id}`);
                  }}
                />
              </div>
              {/* Show thread when conversation is selected */}
              {conversationId ? (
                <div className="md:col-span-2 h-full">
                  <MessageThreadPanel
                    conversationId={conversationId}
                    messages={messages}
                    newMessage={newMessage}
                    setNewMessage={setNewMessage}
                    currentConversation={currentConversation}
                    handleSendMessage={handleSendMessage}
                    isMobile={isMobile}
                    onBack={() => {
                      navigate('/parent-portal/messages');
                    }}
                    currentUser={currentUser}
                  />
                </div>
              ) : (
                <div className="hidden md:flex md:col-span-2 items-center justify-center bg-slate-50 rounded-2xl">
                  <div className="text-center p-6">
                    <Mail className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="font-semibold mb-2 text-gray-700">No conversation selected</h3>
                    <p className="text-sm text-muted-foreground">
                      Select a conversation from the list or start a new one
                    </p>
                  </div>
                </div>
              )}
        </div>
      </div>
    </ParentLayoutUnified>
  );
};

export default ParentPortalMessages;
